import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight, SlidersHorizontal, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { FilterPanel } from "@/components/store/FilterPanel";
import { ProductGrid, SkeletonGrid } from "@/components/store/ProductCard";
import { useShop, useI18n } from "@/context/Shop";
import { useFetch } from "@/hooks/useFetch";
import { useSeo } from "@/hooks/useSeo";
import { media } from "@/lib/api";
import { formatALL } from "@/lib/format";
import { audiencePath, catPath, childrenFor, deptPath } from "@/lib/nav";

const FILTER_KEYS = ["gender", "brand", "category", "size", "color", "material", "min_price", "max_price", "availability", "sale", "new"];

function Crumb({ to, children, last }) {
  return (
    <>
      <ChevronRight className="h-3 w-3 mx-1 shrink-0" />
      {last || !to ? <span className="text-foreground">{children}</span> : <Link to={to} className="hover:text-foreground">{children}</Link>}
    </>
  );
}

export default function Listing({ mode = "catalog", audience = "", department = "" }) {
  const routeParams = useParams();
  const [params, setParams] = useSearchParams();
  const [drawer, setDrawer] = useState(false);
  const { nav } = useShop();
  const { t, tr } = useI18n();

  const routeCategory = routeParams.category || "";
  const query = useMemo(() => {
    const q = { page: params.get("page") || 1, page_size: 24, sort: params.get("sort") || "newest" };
    FILTER_KEYS.forEach((k) => { if (params.get(k)) q[k] = params.get(k); });
    if (audience) q.gender = audience;
    if (department) q.department = department;
    if (routeCategory) q.category = routeCategory;
    if (mode === "collection") q.collection = routeParams.slug;
    if (mode === "brand") q.brand = routeParams.slug;
    if (mode === "sale") q.sale = "true";
    if (mode === "new") q.new = "true";
    if (mode === "search") q.q = params.get("q") || "";
    return q;
  }, [params, audience, department, routeCategory, mode, routeParams.slug]);

  const { data, loading, error } = useFetch("/store/products", query);
  const { data: brand } = useFetch(mode === "brand" ? `/store/brands/${routeParams.slug}` : null, null, { enabled: mode === "brand" });

  const setParam = (...kv) => {
    const next = new URLSearchParams(params);
    for (let i = 0; i < kv.length; i += 2) {
      if (kv[i + 1]) next.set(kv[i], kv[i + 1]); else next.delete(kv[i]);
    }
    if (!kv.includes("page")) next.delete("page");
    setParams(next);
  };
  const clearAll = () => {
    const next = new URLSearchParams();
    if (params.get("q")) next.set("q", params.get("q"));
    if (params.get("sort")) next.set("sort", params.get("sort"));
    setParams(next);
  };

  const meta = data?.meta || {};
  const deptNav = (nav?.departments || []).find((d) => d.slug === department);
  const deptName = meta.department ? tr(meta.department) : deptNav ? tr(deptNav) : "";
  const catName = meta.category ? tr(meta.category) : "";
  const audienceName = audience ? t(audience) : "";
  const title = mode === "brand" ? brand?.name || ""
    : mode === "collection" ? (meta.collection ? tr(meta.collection) : t("collections"))
    : mode === "sale" ? t("sale") : mode === "new" ? t("new_arrivals")
    : mode === "search" ? `“${params.get("q") || ""}”`
    : catName || deptName || audienceName || t("shop_all");
  const subtitle = mode === "brand" ? tr(brand, "description") : mode === "collection" ? tr(meta.collection, "description") : mode === "search" ? t("results_for") : (!routeCategory && meta.department?.description) || "";
  useSeo(mode === "search" ? `${t("search")}: ${params.get("q") || ""}` : [audienceName, deptName, catName].filter(Boolean).join(" · ") || title, subtitle);

  const chips = [];
  FILTER_KEYS.forEach((k) => {
    const v = params.get(k);
    if (!v) return;
    if (["brand", "size", "color", "material"].includes(k)) v.split(",").forEach((x) => chips.push({ k, v: x, label: k === "brand" ? (data?.facets?.brands.find((b) => b.slug === x)?.name || x) : x }));
    else if (k === "min_price" || k === "max_price") chips.push({ k, v, label: `${k === "min_price" ? t("from_price") : t("up_to")} ${formatALL(v)}` });
    else if (k === "gender") chips.push({ k, v, label: t(v) });
    else if (k === "category") chips.push({ k, v, label: data?.facets?.categories.find((c) => c.slug === v) ? tr(data.facets.categories.find((c) => c.slug === v)) : v });
    else chips.push({ k, v, label: { availability: t("in_stock_only"), sale: t("on_sale"), new: t("new_arrivals") }[k] || v });
  });
  const removeChip = (c) => {
    if (["brand", "size", "color", "material"].includes(c.k)) setParam(c.k, params.get(c.k).split(",").filter((x) => x !== c.v).join(","));
    else setParam(c.k, "");
  };
  const hide = [mode === "brand" && "brand", routeCategory && "category", mode === "sale" && "sale", audience && "gender"].filter(Boolean);
  const page = Number(params.get("page") || 1);
  const siblings = deptNav && !routeCategory ? childrenFor(deptNav, audience || null) : [];

  return (
    <div className="container-lux pt-6" data-testid="listing-page">
      <nav className="text-xs text-muted-foreground mb-6 flex items-center flex-wrap" aria-label="Breadcrumb" data-testid="breadcrumbs">
        <Link to="/" className="hover:text-foreground">{t("home")}</Link>
        {audience && <Crumb to={audiencePath(audience)} last={!department && mode === "catalog"}>{audienceName}</Crumb>}
        {department && <Crumb to={deptPath(department, audience)} last={!routeCategory}>{deptName}</Crumb>}
        {routeCategory && <Crumb last>{catName || routeCategory}</Crumb>}
        {mode === "brand" && <><Crumb to="/brands">{t("brands")}</Crumb><Crumb last>{title}</Crumb></>}
        {mode === "collection" && <><Crumb to="/collections">{t("collections")}</Crumb><Crumb last>{title}</Crumb></>}
        {["sale", "new", "search"].includes(mode) && <Crumb last>{title}</Crumb>}
      </nav>

      {mode === "brand" && brand?.hero_image_url && (
        <div className="relative overflow-hidden bg-muted aspect-[21/9] sm:aspect-[3/1] mb-8" data-testid="brand-hero">
          <img src={media(brand.hero_image_url, 1800)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        </div>
      )}
      <header className="mb-8 max-w-3xl">
        {mode === "brand" && brand?.country && <p className="eyebrow mb-2">{brand.country}</p>}
        {audience && department && <p className="eyebrow mb-2">{audienceName}</p>}
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-[-0.01em]" data-testid="listing-title">{title}</h1>
        {subtitle && <p className="text-foreground/70 mt-4 leading-relaxed text-sm sm:text-base">{subtitle}</p>}
      </header>

      {siblings.length > 0 && (
        <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1 mb-6 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="listing-subcategories">
          {siblings.map((c) => <Link key={c.slug} to={catPath(department, c.slug, audience)} className="shrink-0 border px-3.5 py-2 text-[12px] hover:bg-foreground hover:text-background transition-colors" data-testid={`listing-subcategory-${c.slug}`}>{tr(c)}</Link>)}
        </div>
      )}

      <div className="lg:flex lg:gap-12">
        <aside className="hidden lg:block lg:w-[280px] lg:shrink-0" aria-label={t("filters")}>
          <div className="sticky top-[124px] max-h-[calc(100vh-9rem)] overflow-y-auto pr-2 scrollbar-none">
            <FilterPanel facets={data?.facets} params={params} setParam={setParam} hide={hide} />
          </div>
        </aside>
        <div className="flex-1 min-w-0">
          <div className="sticky top-16 lg:top-[108px] z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 border-b -mx-4 px-4 sm:mx-0 sm:px-0 py-3 mb-6 flex items-center justify-between gap-3">
            <button className="lg:hidden inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.14em] font-semibold" onClick={() => setDrawer(true)} data-testid="plp-filter-open-button"><SlidersHorizontal className="h-4 w-4" /> {t("filters")} {chips.length > 0 && `(${chips.length})`}</button>
            <p className="text-sm text-muted-foreground hidden lg:block tabular" data-testid="listing-count">{loading ? t("loading") : `${data?.total ?? 0} ${data?.total === 1 ? t("item") : t("items")}`}</p>
            <Select value={params.get("sort") || "newest"} onValueChange={(v) => setParam("sort", v)}>
              <SelectTrigger className="w-[200px] h-10 rounded-none bg-background" data-testid="plp-sort-select"><SelectValue placeholder={t("sort")} /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="newest" data-testid="plp-sort-newest">{t("newest")}</SelectItem>
                <SelectItem value="price_asc" data-testid="plp-sort-price-asc">{t("price_asc")}</SelectItem>
                <SelectItem value="price_desc" data-testid="plp-sort-price-desc">{t("price_desc")}</SelectItem>
                <SelectItem value="name" data-testid="plp-sort-name">{t("brand_az")}</SelectItem>
                <SelectItem value="discount" data-testid="plp-sort-discount">{t("discount")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {chips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="applied-filters">
              {chips.map((c) => <button key={c.k + c.v} onClick={() => removeChip(c)} className="inline-flex items-center gap-2 border px-3 py-1.5 text-xs hover:border-foreground" data-testid="applied-filter-chip">{c.label}<X className="h-3 w-3" /></button>)}
              <button onClick={clearAll} className="text-xs link-lux ml-2" data-testid="plp-reset-filters-button">{t("clear_all")}</button>
            </div>
          )}
          {error ? <p className="text-sm text-[hsl(var(--sale))]">{error}</p> : loading && !data ? <SkeletonGrid /> : data?.items?.length ? (
            <div className={loading ? "opacity-60 transition-opacity" : ""}><ProductGrid items={data.items} eagerCount={4} /></div>
          ) : (
            <div className="py-16" data-testid="listing-empty">
              <div className="text-center">
                <p className="font-display text-3xl">{t("no_results_title")}</p>
                <p className="text-sm text-muted-foreground mt-2">{t("no_results_sub")}</p>
                {data?.did_you_mean && <p className="text-sm mt-4" data-testid="listing-did-you-mean">{t("did_you_mean")} <Link to={`/search?q=${encodeURIComponent(data.did_you_mean)}`} className="font-semibold underline underline-offset-4">{data.did_you_mean}</Link>?</p>}
                <div className="flex justify-center gap-3 mt-6">{chips.length > 0 && <button onClick={clearAll} className="btn-ink">{t("clear_filters")}</button>}<Link to="/new-arrivals" className="btn-ghost-lux">{t("new_arrivals")}</Link></div>
              </div>
              {data?.alternatives?.length > 0 && <div className="mt-16"><h2 className="font-display text-2xl mb-6">{t("alternatives")}</h2><ProductGrid items={data.alternatives} testid="listing-alternatives" /></div>}
            </div>
          )}
          {data?.pages > 1 && (
            <nav className="flex items-center justify-center gap-2 mt-16" aria-label="Pagination" data-testid="plp-pagination">
              <button disabled={page <= 1} onClick={() => setParam("page", String(page - 1))} className="h-10 px-4 border text-sm disabled:opacity-30" data-testid="plp-prev-page-button">{t("previous")}</button>
              {Array.from({ length: data.pages }).map((_, i) => <button key={i} onClick={() => setParam("page", String(i + 1))} aria-current={page === i + 1 ? "page" : undefined} className={`h-10 w-10 text-sm tabular ${page === i + 1 ? "bg-foreground text-background" : "hover:bg-secondary"}`} data-testid={`plp-page-${i + 1}-button`}>{i + 1}</button>)}
              <button disabled={page >= data.pages} onClick={() => setParam("page", String(page + 1))} className="h-10 px-4 border text-sm disabled:opacity-30" data-testid="plp-next-page-button">{t("next")}</button>
            </nav>
          )}
        </div>
      </div>

      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="right" className="w-[90vw] sm:max-w-sm bg-background flex flex-col p-0 rounded-none" data-testid="plp-filter-drawer">
          <SheetHeader className="px-6 py-5 border-b"><SheetTitle className="font-display text-2xl text-left">{t("filters")}</SheetTitle></SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4"><FilterPanel facets={data?.facets} params={params} setParam={setParam} hide={hide} /></div>
          <div className="border-t px-6 py-4 flex gap-3 bg-background">
            <button className="btn-ghost-lux flex-1 h-11" onClick={clearAll} data-testid="plp-drawer-reset-button">{t("reset")}</button>
            <button className="btn-ink flex-1 h-11" onClick={() => setDrawer(false)} data-testid="plp-filter-apply-button">{t("show")} {data?.total ?? 0}</button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
